import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from "react";
import { units } from "@m/alchemy-theme";
import { useSlateStatic } from "slate-react";
import { Checkbox } from "../../../checkbox/Checkbox";
import { UnlinkIcon } from "../../../../icons/UnlinkIcon";
import { EditIcon } from "../../../../icons/EditIcon";
import { useSlateRTE } from "../../SlateRTEContext";
import { Input } from "../../../input/Input";
import { Field } from "../../../field/Field";
import { CancelXIcon } from "../../../../icons/CancelXIcon";
import { ConfirmCheckmarkIcon } from "../../../../icons/ConfirmCheckmarkIcon";
import { focusEditor } from "../../utilities/index";
import { ToolbarLink } from "../../../../toolbar/ToolbarLink";
import { Toolbar } from "../../../../toolbar/Toolbar";
import { ToolbarButton } from "../../../../toolbar/ToolbarButton";
import { ToolbarGroup } from "../../../../toolbar/ToolbarGroup";
import { removeLink, updateLink } from "./link-utilities";
export const LinkToolbar = forwardRef((props, ref) => {
    const { element, ...toolbarProps } = props;
    const { stringMap } = useSlateRTE();
    const editor = useSlateStatic();
    const [isEditing, setIsEditing] = useState(false);
    const [href, setHref] = useState(element.href);
    const [openInNewTab, setOpenInNewTab] = useState(element?.openInNewTab ?? false);
    const inputRef = useRef(null);
    const linkRef = useRef(null);
    const wasEditingRef = useRef(false);
    useEffect(() => {
        if (wasEditingRef.current && linkRef.current) {
            // return focus back to link after editing
            linkRef.current.focus();
        }
        else if (inputRef.current) {
            // autofocus on edit
            inputRef.current.setSelectionRange(0, 0);
            inputRef.current.focus();
            inputRef.current.select();
        }
        wasEditingRef.current = isEditing;
    }, [isEditing]);
    return (_jsx("div", { "data-testid": "LinkToolbar", ref: ref, children: isEditing ? (_jsxs(Toolbar, { as: "form", onSubmit: (e) => {
                e.preventDefault();
                e.stopPropagation();
                updateLink({ editor, element, href, openInNewTab });
                setIsEditing(false);
            }, ...toolbarProps, children: [_jsxs(ToolbarGroup, { children: [_jsx(Field, { label: stringMap.linkHref, required: true, input: _jsx(Input, { value: href, ref: inputRef, onKeyDown: (e) => {
                                    if (/Arrow/.test(e.key)) {
                                        // Prevent focus list behavior
                                        e.stopPropagation();
                                    }
                                }, onChange: (e) => setHref(e.currentTarget.value) }), layoutVariant: "inline", style: { marginLeft: units(1), marginRight: units(1) } }), _jsx(Checkbox, { checked: openInNewTab, label: stringMap.openInNewTab, onChange: (e) => setOpenInNewTab(e.currentTarget.checked) })] }), _jsxs(ToolbarGroup, { children: [_jsx(ToolbarButton, { label: stringMap.updateLink, icon: _jsx(ConfirmCheckmarkIcon, { "aria-hidden": "true" }), type: "submit" }), _jsx(ToolbarButton, { label: stringMap.cancel, icon: _jsx(CancelXIcon, { "aria-hidden": "true" }), onClick: () => {
                                setIsEditing(false);
                                setHref(element.href);
                                setOpenInNewTab(element.openInNewTab ?? false);
                            } })] })] })) : (_jsxs(Toolbar, { ...toolbarProps, children: [_jsx(ToolbarGroup, { children: _jsx(ToolbarLink, { href: element.href, target: "_blank", ref: linkRef, children: element.href }) }), _jsxs(ToolbarGroup, { children: [_jsx(ToolbarButton, { label: stringMap.editLink, icon: _jsx(EditIcon, { "aria-hidden": "true" }), onClick: () => setIsEditing(true) }), _jsx(ToolbarButton, { label: stringMap.removeLink, icon: _jsx(UnlinkIcon, { "aria-hidden": "true" }), onClick: () => {
                                removeLink(editor, { element });
                                focusEditor(editor);
                            } })] })] })) }));
});
